import { useState } from 'react';
import { motion } from 'framer-motion';
import {
  Building2,
  Lock,
  Flag,
  Cpu,
  Gift,
  PhoneCall,
  Trophy,
  PlayCircle,
  CheckCircle,
  CalendarClock,
  ArrowRight
} from 'lucide-react';
import {
  AnimatedSection,
  StaggerContainer,
  StaggerItem,
  ScaleIn
} from './AnimatedSection';

/**
 * Replace with the real Loom share ID once the demo is recorded.
 * Embed URL format: https://www.loom.com/embed/<LOOM_ID>
 */
const LOOM_ID = 'c4c37ae6aa1047c79a690c82a3cb2b49';

/* Number of spots left — hardcoded for now, will be wired to the API later. */
const SPOTS_REMAINING = 17;

/* ------------------------------------------------------------------ */
/* Trust bar — sits just below the hero                               */
/* ------------------------------------------------------------------ */

const trustItems = [
  { icon: Building2, text: 'Built for every medical specialty' },
  { icon: Lock, text: 'HDS-compliant architecture' },
  { icon: Flag, text: 'Made in France' },
  { icon: Cpu, text: 'Powered by local LLMs — your data never leaves your server' },
];

export const TrustBar = ({ dir = 'ltr' }: { dir?: string }) => {
  return (
    <section className="border-y border-sand-300 bg-sand-50/80" dir={dir}>
      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-6">
        <StaggerContainer className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
          {trustItems.map((item, index) => (
            <StaggerItem key={index}>
              <div className="flex items-center gap-3 justify-center lg:justify-start text-center lg:text-start">
                <div className="w-9 h-9 rounded-lg bg-forest/10 flex items-center justify-center flex-shrink-0">
                  <item.icon className="w-5 h-5 text-forest" />
                </div>
                <span className="text-sm font-medium text-bark-700/80">{item.text}</span>
              </div>
            </StaggerItem>
          ))}
        </StaggerContainer>
      </div>
    </section>
  );
};

/* ------------------------------------------------------------------ */
/* Beta perks                                                          */
/* ------------------------------------------------------------------ */

const perks = [
  {
    icon: Gift,
    title: 'Free lifetime access',
    description: 'Early testers keep MedKit.ai free forever — no card, no expiry.',
  },
  {
    icon: PhoneCall,
    title: 'Direct line to the founder',
    description: 'Skip support queues. Talk straight to the person building it.',
  },
  {
    icon: Trophy,
    title: 'Shape the product roadmap',
    description: 'Your feedback decides what we build next for the whole medical field.',
  },
];

/* ------------------------------------------------------------------ */
/* Signup form                                                         */
/* ------------------------------------------------------------------ */

const MAX_MESSAGE = 200;

type Status = 'idle' | 'submitting' | 'success' | 'error';

interface FormState {
  name: string;
  email: string;
  specialty: string;
  message: string;
}

const initialForm: FormState = {
  name: '',
  email: '',
  specialty: 'Médecin généraliste',
  message: '',
};

export const BetaProgram = ({ dir = 'ltr' }: { dir?: string }) => {
  const [form, setForm] = useState<FormState>(initialForm);
  const [status, setStatus] = useState<Status>('idle');
  const [errorMessage, setErrorMessage] = useState('');

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    if (name === 'message' && value.length > MAX_MESSAGE) return;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus('submitting');
    setErrorMessage('');

    try {
      const res = await fetch('/api/beta-signup', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok || data.status !== 'success') {
        setStatus('error');
        setErrorMessage(
          data.message || 'Something went wrong. Please try again or email us directly.'
        );
        return;
      }

      setStatus('success');
    } catch {
      setStatus('error');
      setErrorMessage('Could not reach the server. Please try again in a moment.');
    }
  };

  return (
    <section
      id="beta"
      className="relative py-20 lg:py-28 bg-gradient-to-b from-sand-50 to-sand-300/30 overflow-hidden"
      dir={dir}
    >
      {/* Soft background accents */}
      <div className="absolute top-10 -end-32 w-[500px] h-[500px] bg-forest/10 rounded-full blur-3xl" />
      <div className="absolute bottom-0 -start-32 w-[400px] h-[400px] bg-accent/10 rounded-full blur-3xl" />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
        {/* ---------------------------------------------------------- */}
        {/* Demo embed: "See It In Action"                             */}
        {/* ---------------------------------------------------------- */}
        <AnimatedSection id="demo" className="scroll-mt-28 text-center mb-12 lg:mb-16">
          <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-forest/10 text-forest text-sm font-medium mb-4">
            <PlayCircle className="w-4 h-4" />
            See It In Action
          </span>
          <h2 className="text-3xl lg:text-5xl font-bold text-bark-700 mb-4 max-w-3xl mx-auto">
            Watch a Real Consultation Being{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-forest to-forest-400">
              Documented in Real Time
            </span>
          </h2>
        </AnimatedSection>

        <ScaleIn className="max-w-4xl mx-auto mb-20 lg:mb-28">
          <div className="bg-sand-50 rounded-3xl p-3 lg:p-4 shadow-2xl shadow-bark-700/10 border border-sand-300">
            <div className="relative w-full overflow-hidden rounded-2xl bg-bark-700" style={{ paddingTop: '56.25%' }}>
              <iframe
                src={`https://www.loom.com/embed/${LOOM_ID}`}
                title="MedKit.ai live consultation demo"
                frameBorder={0}
                allowFullScreen
                className="absolute inset-0 w-full h-full"
              />
            </div>
          </div>
          <p className="text-center text-bark-700/60 text-sm mt-4">
            2-minute demo — no account needed
          </p>
        </ScaleIn>

        {/* ---------------------------------------------------------- */}
        {/* Beta headline + scarcity                                   */}
        {/* ---------------------------------------------------------- */}
        <AnimatedSection className="text-center mb-12 lg:mb-16">
          <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-red-50 text-red-600 text-sm font-medium mb-4">
            <CalendarClock className="w-4 h-4" />
            Beta closes July 31, 2025
          </span>
          <h2 className="text-3xl lg:text-5xl font-bold text-bark-700 mb-4 max-w-3xl mx-auto">
            Be Among the First 20 Doctors to{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-forest to-forest-400">
              Try MedKit.ai
            </span>
          </h2>
          <p className="text-xl text-bark-700/60 max-w-2xl mx-auto mb-6">
            Free lifetime access for early testers. Just 30 min/month of feedback.
          </p>
          <div className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-red-50 border border-red-100 text-red-600 font-semibold">
            <span className="relative flex h-2.5 w-2.5">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75" />
              <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-red-500" />
            </span>
            {SPOTS_REMAINING} spots remaining
          </div>
        </AnimatedSection>

        {/* ---------------------------------------------------------- */}
        {/* Perks                                                      */}
        {/* ---------------------------------------------------------- */}
        <StaggerContainer className="grid md:grid-cols-3 gap-6 lg:gap-8 mb-16 max-w-5xl mx-auto">
          {perks.map((perk, index) => (
            <StaggerItem key={index}>
              <div className="h-full bg-sand-50 rounded-2xl p-8 border border-sand-300 hover:border-forest/30 hover:shadow-lg transition-all text-center">
                <div className="w-14 h-14 rounded-xl bg-forest/10 flex items-center justify-center mx-auto mb-5">
                  <perk.icon className="w-7 h-7 text-forest" />
                </div>
                <h3 className="text-lg font-bold text-bark-700 mb-2">{perk.title}</h3>
                <p className="text-bark-700/60 text-sm leading-relaxed">{perk.description}</p>
              </div>
            </StaggerItem>
          ))}
        </StaggerContainer>

        {/* ---------------------------------------------------------- */}
        {/* Signup form                                                */}
        {/* ---------------------------------------------------------- */}
        <AnimatedSection className="max-w-2xl mx-auto">
          <div className="bg-sand-50 rounded-3xl p-8 lg:p-12 shadow-xl border border-sand-300">
            {status === 'success' ? (
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="text-center py-8"
              >
                <div className="w-20 h-20 rounded-full bg-forest/10 flex items-center justify-center mx-auto mb-6">
                  <CheckCircle className="w-10 h-10 text-forest" />
                </div>
                <h3 className="text-2xl font-bold text-bark-700 mb-3">
                  You're on the list!
                </h3>
                <p className="text-bark-700/60 text-lg">
                  We'll be in touch within 48h ✅
                </p>
              </motion.div>
            ) : (
              <>
                <h3 className="text-2xl lg:text-3xl font-bold text-bark-700 mb-2 text-center">
                  Claim Your Beta Spot
                </h3>
                <p className="text-bark-700/60 mb-8 text-center">
                  Reserve one of the {SPOTS_REMAINING} remaining places.
                </p>

                <form onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-bark-700 mb-2">
                      Full name *
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={form.name}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 rounded-xl border border-sand-300 focus:ring-2 focus:ring-forest/20 focus:border-forest outline-none transition-all bg-sand-50"
                      placeholder="Dr. Marie Dubois"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-bark-700 mb-2">
                      Email address *
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={form.email}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 rounded-xl border border-sand-300 focus:ring-2 focus:ring-forest/20 focus:border-forest outline-none transition-all bg-sand-50"
                      placeholder="marie@clinique.fr"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-bark-700 mb-2">
                      Specialty / Role *
                    </label>
                    <select
                      name="specialty"
                      value={form.specialty}
                      onChange={handleChange}
                      className="w-full px-4 py-3 rounded-xl border border-sand-300 focus:ring-2 focus:ring-forest/20 focus:border-forest outline-none transition-all bg-sand-50"
                    >
                      <option value="Médecin généraliste">Médecin généraliste</option>
                      <option value="Médecin spécialiste">Médecin spécialiste</option>
                      <option value="Médecin du travail">Médecin du travail</option>
                      <option value="Autre professionnel de santé">Autre professionnel de santé</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-bark-700 mb-2">
                      Why are you interested?{' '}
                      <span className="text-bark-700/40 font-normal">(optional)</span>
                    </label>
                    <textarea
                      name="message"
                      value={form.message}
                      onChange={handleChange}
                      rows={3}
                      maxLength={MAX_MESSAGE}
                      className="w-full px-4 py-3 rounded-xl border border-sand-300 focus:ring-2 focus:ring-forest/20 focus:border-forest outline-none transition-all resize-none bg-sand-50"
                      placeholder="What would make MedKit.ai useful for your practice?"
                    />
                    <p className="text-right text-xs text-bark-700/40 mt-1">
                      {form.message.length}/{MAX_MESSAGE}
                    </p>
                  </div>

                  {status === 'error' && (
                    <p className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-xl px-4 py-3">
                      {errorMessage}
                    </p>
                  )}

                  <button
                    type="submit"
                    disabled={status === 'submitting'}
                    className="w-full py-4 rounded-xl bg-bark-700 text-white font-semibold hover:bg-bark-600 transition-all shadow-lg shadow-bark-700/25 flex items-center justify-center gap-2 disabled:opacity-70"
                  >
                    {status === 'submitting' ? (
                      <>
                        <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        Reserving your spot...
                      </>
                    ) : (
                      <>
                        Claim My Beta Spot
                        <ArrowRight className="w-5 h-5" />
                      </>
                    )}
                  </button>

                  <p className="text-center text-xs text-bark-700/40">
                    No spam. We'll only email you about the beta.
                  </p>
                </form>
              </>
            )}
          </div>
        </AnimatedSection>
      </div>
    </section>
  );
};
